import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { Calendar, Clock, MapPin, Phone, Mail, ChevronLeft, ChevronRight, Heart, Activity, Eye, Baby, Siren, Globe, Menu, X, Smartphone, Copy, ChevronDown, ChevronUp, Stethoscope, AlertCircle, Syringe, Pill, Thermometer, Bandage, Microscope, Scissors, Cross, Brain, Bone, Ear, TestTube, CircleDot, User, UserCheck, Venus, Mars } from 'lucide-react';

const POCKETBASE_URL = 'https://medvista.online';
const POCKETBASE_USERNAME = 'app-access-01@medvista.online';
const POCKETBASE_PASSWORD = '9ygc90ezomgt82l';

let authToken = null;

const authenticatePocketBase = async () => {
  if (authToken) return authToken;
  
  try {
    const response = await fetch(`${POCKETBASE_URL}/api/collections/_superusers/auth-with-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        identity: POCKETBASE_USERNAME,
        password: POCKETBASE_PASSWORD
      })
    });
    
    if (!response.ok) {
      throw new Error('Authentication failed');
    }
    
    const data = await response.json();
    authToken = data.token;
    return authToken;
  } catch (error) {
    console.error('Auth error:', error);
    return null;
  }
};

const validateAndGetTenantToken = async (tenantName) => {
  try {
    const token = await authenticatePocketBase();
    if (!token) {
      throw new Error('Authentication failed');
    }

    console.log('Validating tenant:', tenantName);
    
    const response = await fetch(
      `${POCKETBASE_URL}/api/collections/tenant_list_table/records?filter=(tenant_name='${tenantName}')`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch tenant data');
    }

    const result = await response.json();
    console.log('Tenant validation result:', result);

    if (result.items && result.items.length > 0) {
      const tenantData = result.items[0];
      
      if (tenantData.is_tenant_active === 'true' || tenantData.is_tenant_active === true) {
        return {
          isActive: true,
          tenantToken: tenantData.tenant_token,
          tenantName: tenantData.tenant_name
        };
      } else {
        return {
          isActive: false,
          error: 'Tenant is not active'
        };
      }
    } else {
      return {
        isActive: false,
        error: 'Tenant not found'
      };
    }
  } catch (error) {
    console.error('Error validating tenant:', error);
    return {
      isActive: false,
      error: error.message
    };
  }
};

const parseWorkingHours = (dayString) => {
  if (!dayString || dayString.trim() === '') {
    return { open: null, close: null, isOpen: false };
  }
  
  const match = dayString.match(/(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
  if (match) {
    return { open: match[1], close: match[2], isOpen: true };
  }
  
  return { open: null, close: null, isOpen: false };
};

const getTenantNameFromUrl = () => {
  const path = window.location.pathname;
  const segments = path.split('/').filter(s => s);
  return segments[0] || null;
};

// Proširena lista medicinskih ikonica sa Venus i Mars
const getIconForService = (iconName) => {
  const iconMap = {
    'Heart': Heart,
    'Activity': Activity,
    'Eye': Eye,
    'Baby': Baby,
    'Siren': Siren,
    'Stethoscope': Stethoscope,
    'Syringe': Syringe,
    'Pill': Pill,
    'Thermometer': Thermometer,
    'Bandage': Bandage,
    'Microscope': Microscope,
    'Scissors': Scissors,
    'Cross': Cross,
    'Brain': Brain,
    'Bone': Bone,
    'Ear': Ear,
    'TestTube': TestTube,
    'CircleDot': CircleDot,
    'Venus': Venus,
    'Mars': Mars
  };
  return iconMap[iconName] || Stethoscope;
};

const defaultClinicInfo = {
  name: "Učitavanje...",
  slogan: "",
  about: "",
  address: "",
  place: "",
  location: "",
  country: "",
  zip: "",
  googleMapsUrl: "",
  phone1: "",
  phone2: "",
  email: "",
  website: "",
  logo: null,
  logoUrl: null,
  alert: "",
  messages: {
    confirmationNote: "Molimo sačuvajte ID rezervacije. Dođite 10 minuta pre zakazanog termina.",
    confirmationTitle: "Pregled je zakazan!",
    confirmationSubtitle: "Potvrda je poslata"
  },
  workingHours: {
    ponedeljak: { open: null, close: null, isOpen: false },
    utorak: { open: null, close: null, isOpen: false },
    sreda: { open: null, close: null, isOpen: false },
    četvrtak: { open: null, close: null, isOpen: false },
    petak: { open: null, close: null, isOpen: false },
    subota: { open: null, close: null, isOpen: false },
    nedelja: { open: null, close: null, isOpen: false }
  }
};

const generateAppointments = (workingHours, duration) => {
  const appointments = [];
  const today = new Date();
  const appointmentDuration = parseInt(duration) || 30;
  
  for (let day = 0; day < 14; day++) {
    const date = new Date(today);
    date.setDate(today.getDate() + day);
    const dateStr = date.toISOString().split('T')[0];
    const dayName = ['nedelja', 'ponedeljak', 'utorak', 'sreda', 'četvrtak', 'petak', 'subota'][date.getDay()];
    const workingDay = workingHours[dayName];
    
    if (!workingDay || !workingDay.isOpen) continue;
    
    const [openHour] = workingDay.open.split(':').map(Number);
    const [closeHour, closeMin] = workingDay.close.split(':').map(Number);
    
    for (let hour = openHour; hour < closeHour; hour++) {
      for (let min = 0; min < 60; min += appointmentDuration) {
        if (hour === closeHour - 1 && min >= closeMin) break;
        
        const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        const isAvailable = Math.random() > 0.35;
        
        appointments.push({
          id: `${dateStr}-${timeStr}`,
          date: dateStr,
          time: timeStr,
          available: isAvailable
        });
      }
    }
  }
  
  return appointments;
};

const generateBookingId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = '';
  for (let i = 0; i < 8; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
};

const App = () => {
  const [clinicInfo, setClinicInfo] = useState(defaultClinicInfo);
  const [tenantName, setTenantName] = useState('');
  const [tenantToken, setTenantToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [tenantNotFound, setTenantNotFound] = useState(false);
  const [tenantInactive, setTenantInactive] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentPage, setCurrentPage] = useState('services');
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthYear: '',
    phone: '',
    email: '',
    notes: ''
  });

  useEffect(() => {
    const loadClinicData = async () => {
      try {
        const tenant = getTenantNameFromUrl();
        
        if (!tenant) {
          setTenantNotFound(true);
          setLoading(false);
          return;
        }
        
        setTenantName(tenant);
        
        console.log('Tenant name:', tenant);
        console.log('Attempting to authenticate...');
        
        const token = await authenticatePocketBase();
        if (!token) {
          console.error('Authentication failed - no token received');
          setClinicInfo({
            ...defaultClinicInfo,
            name: "Greška - Autentifikacija neuspešna"
          });
          setLoading(false);
          return;
        }
        
        console.log('Authentication successful');
        
        console.log('Validating tenant and fetching token...');
        const tenantValidation = await validateAndGetTenantToken(tenant);
        
        if (!tenantValidation.isActive) {
          console.error('Tenant is not active:', tenantValidation.error);
          setTenantInactive(true);
          setLoading(false);
          return;
        }
        
        console.log('Tenant is active. Token:', tenantValidation.tenantToken);
        setTenantToken(tenantValidation.tenantToken);
        
        console.log('Fetching clinic data for tenant:', tenant);
        
        const response = await fetch(
          `${POCKETBASE_URL}/api/collections/tenant_details_table/records?filter=(tenant_name='${tenant}')`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token
            }
          }
        );
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to fetch clinic data:', errorText);
          throw new Error('Failed to fetch clinic data');
        }
        
        const result = await response.json();
        console.log('Received data:', result);
        
        if (result.items && result.items.length > 0) {
          const data = result.items[0];
          
          let logoUrl = null;
          if (data.logo) {
            logoUrl = `${POCKETBASE_URL}/api/files/${data.collectionId}/${data.id}/${data.logo}`;
          }
          
          const mappedInfo = {
            name: data.name || "Klinika",
            slogan: data.description || "",
            about: data.about || "",
            address: data.address || "",
            place: data.place || "",
            location: `${data.address || ''}${data.address && data.place ? ', ' : ''}${data.place || ''}`,
            country: data.country || "",
            zip: data.zip || "",
            googleMapsUrl: data.address && data.place ? `https://maps.google.com/?q=${encodeURIComponent(data.address + ', ' + data.place)}` : "",
            phone1: data.telephone || "",
            phone2: data.telephone2 || "",
            email: data.email || "",
            website: data.web || "",
            logo: data.logo || null,
            logoUrl: logoUrl,
            alert: data.alert || "",
            messages: {
              confirmationNote: "Molimo sačuvajte ID rezervacije. Dođite 10 minuta pre zakazanog termina.",
              confirmationTitle: "Pregled je zakazan!",
              confirmationSubtitle: "Potvrda je poslata"
            },
            workingHours: {
              ponedeljak: parseWorkingHours(data.monday),
              utorak: parseWorkingHours(data.tuesday),
              sreda: parseWorkingHours(data.wednesday),
              četvrtak: parseWorkingHours(data.thursday),
              petak: parseWorkingHours(data.friday),
              subota: parseWorkingHours(data.saturday),
              nedelja: parseWorkingHours(data.sunday)
            }
          };
          
          console.log('Mapped clinic info:', mappedInfo);
          
          setClinicInfo(mappedInfo);

          console.log('Fetching services for tenant:', tenant);
          const servicesResponse = await fetch(
            `${POCKETBASE_URL}/api/collections/tenant_services_table/records?filter=(tenant_name='${tenant}')`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': token
              }
            }
          );

          if (servicesResponse.ok) {
            const servicesResult = await servicesResponse.json();
            console.log('Services data:', servicesResult);

            if (servicesResult.items && servicesResult.items.length > 0) {
              // Prikazujemo sve usluge, bez obzira da li imaju duration
              const mappedServices = servicesResult.items.map((service, index) => ({
                id: service.tenant_item_id || service.id || index + 1,
                name: service.name || "Usluga",
                description: service.description || "",
                duration: service.duration && service.duration.trim() !== '' ? parseInt(service.duration) : null,
                price: service.price || null,
                icon: getIconForService(service.picture),
                color: service.color || "blue"
              }));

              console.log('Mapped services:', mappedServices);
              setServices(mappedServices);
            } else {
              console.warn('No services found for tenant:', tenant);
              setServices([]);
            }
          } else {
            console.error('Failed to fetch services');
            setServices([]);
          }
        } else {
          console.warn('No clinic data found for tenant:', tenant);
          setTenantNotFound(true);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading clinic data:', error);
        setClinicInfo({
          ...defaultClinicInfo,
          name: "Greška pri učitavanju"
        });
        setLoading(false);
      }
    };
    
    loadClinicData();
  }, []);

  // Generisanje QR koda kada se učitaju podaci klinike
  useEffect(() => {
    const generateQRCode = async () => {
      if (!clinicInfo.name || clinicInfo.name === "Učitavanje..." || clinicInfo.name === "Greška pri učitavanju") {
        return;
      }

      // VCard 3.0 format - ADR format: ;;street;city;region;postal;country
      const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${clinicInfo.name}
ORG:${clinicInfo.name}
${clinicInfo.phone1 ? `TEL;TYPE=WORK,VOICE:${clinicInfo.phone1}` : ''}
${clinicInfo.phone2 ? `TEL;TYPE=WORK,VOICE:${clinicInfo.phone2}` : ''}
${clinicInfo.email ? `EMAIL;TYPE=WORK:${clinicInfo.email}` : ''}
${clinicInfo.address || clinicInfo.place || clinicInfo.zip || clinicInfo.country ? `ADR;TYPE=WORK:;;${clinicInfo.address || ''};${clinicInfo.place || ''};;${clinicInfo.zip || ''};${clinicInfo.country || ''}` : ''}
${clinicInfo.website ? `URL;TYPE=HOME:https://${clinicInfo.website}` : ''}
${tenantName ? `URL;TYPE=WORK:https://medvista.online/${tenantName}` : ''}
${clinicInfo.slogan ? `NOTE:${clinicInfo.slogan}` : ''}
END:VCARD`;

      try {
        const qrDataUrl = await QRCode.toDataURL(vcard, {
          width: 400,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeDataUrl(qrDataUrl);
      } catch (err) {
        console.error('Error generating QR code:', err);
      }
    };

    generateQRCode();
  }, [clinicInfo, tenantName]);

  const loadProvidersForService = async (serviceTenantItemId) => {
    try {
      const token = await authenticatePocketBase();
      if (!token) {
        throw new Error('Authentication failed');
      }

      console.log('Fetching providers for service tenant_item_id:', serviceTenantItemId, 'and tenant:', tenantName);
      
      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_providers_table/records?filter=(tenant_name='${tenantName}')`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch providers');
      }

      const result = await response.json();
      console.log('Providers data:', result);

      if (result.items && result.items.length > 0) {
        // Filtriramo pruzaoce koji nude ovu uslugu (koristi tenant_item_id)
        const filteredProviders = result.items.filter(provider => {
          if (!provider.service_ids) return false;
          const serviceIds = provider.service_ids.split(',').map(id => id.trim());
          return serviceIds.includes(serviceTenantItemId.toString()) && provider.is_active === 'true';
        });

        const mappedProviders = filteredProviders.map(provider => {
          return {
            id: provider.provider_id || provider.id,
            firstName: provider.first_name || "",
            lastName: provider.last_name || "",
            title: provider.title || "",
            specialization: provider.specialization || "",
            duration: provider.duration || "30",
            calendarId: provider.calendar_id || ""
          };
        });

        console.log('Mapped providers:', mappedProviders);
        setProviders(mappedProviders);
      } else {
        console.warn('No providers found for service:', serviceTenantItemId);
        setProviders([]);
      }
    } catch (error) {
      console.error('Error loading providers:', error);
      setProviders([]);
    }
  };

  const availableDates = [...new Set(appointments.map(a => a.date))];
  const currentDateIndex = availableDates.indexOf(selectedDate);
  const filteredAppointments = appointments.filter(apt => apt.date === selectedDate);

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.birthYear || !formData.phone) {
      setValidationError('Molimo popunite sva obavezna polja (Ime, Prezime, Godište, Telefon)');
      return false;
    }
    
    if (!/^\d{4}$/.test(formData.birthYear)) {
      setValidationError('Godište mora biti 4 cifre (npr. 1990)');
      return false;
    }
    
    const year = parseInt(formData.birthYear);
    if (year < 1900 || year > new Date().getFullYear()) {
      setValidationError('Molimo unesite validnu godinu rođenja');
      return false;
    }
    
    if (!/^\d+$/.test(formData.phone.replace(/\s/g, ''))) {
      setValidationError('Broj telefona može sadržati samo brojeve');
      return false;
    }
    
    setValidationError('');
    return true;
  };

  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    setSelectedProvider(null);
    await loadProvidersForService(service.id);
    setCurrentPage('providers');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    // Generisemo termine sa trajanjem od pruzaoca
    setAppointments(generateAppointments(clinicInfo.workingHours, provider.duration));
    setCurrentPage('calendar');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAppointmentClick = (appointment) => {
    if (appointment.available) {
      setSelectedAppointment(appointment);
      setCurrentPage('form');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    const newBookingId = generateBookingId();
    setBookingId(newBookingId);
    
    // Koristimo duration od pruzaoca
    const providerDuration = parseInt(selectedProvider.duration) || 30;
    const endTime = new Date(`2000-01-01 ${selectedAppointment.time}`);
    endTime.setMinutes(endTime.getMinutes() + providerDuration);
    const endTimeStr = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;
    
    const providerFullName = `${selectedProvider.title ? selectedProvider.title + ' ' : ''}${selectedProvider.firstName} ${selectedProvider.lastName}`.trim();
    
    const bookingData = {
      tenant_name: tenantName,
      book_id: newBookingId,
      name: formData.firstName,
      surname: formData.lastName,
      date_of_birth: formData.birthYear,
      phone: formData.phone,
      email: formData.email || '',
      note: formData.notes || '',
      service_type_id: selectedService.id.toString(),
      service_type_rendered: selectedService.name,
      provider_id: selectedProvider.id,
      provider_name: providerFullName,
      schedule_date: selectedAppointment.date,
      schedule_start_time: selectedAppointment.time,
      schedule_end_time: endTimeStr,
      synced: 'false',
      tenant_token: tenantToken
    };
    
    console.log('Booking data:', bookingData);
    
    try {
      const token = await authenticatePocketBase();
      if (!token) {
        throw new Error('Failed to authenticate');
      }
      
      const response = await fetch(
        `${POCKETBASE_URL}/api/collections/tenant_booked_slots_table/records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(bookingData)
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to save booking:', errorText);
        throw new Error('Failed to save booking');
      }
      
      const savedBooking = await response.json();
      console.log('Booking saved successfully:', savedBooking);
      
      setCurrentPage('confirmation');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving booking:', error);
      setValidationError('Greška pri čuvanju rezervacije. Molimo pokušajte ponovo.');
    }
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setValidationError('');
  };

  const resetBooking = () => {
    setCurrentPage('services');
    setSelectedService(null);
    setSelectedProvider(null);
    setSelectedAppointment(null);
    setBookingId(null);
    setValidationError('');
    setFormData({
      firstName: '',
      lastName: '',
      birthYear: '',
      phone: '',
      email: '',
      notes: ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToHome = () => {
    setCurrentPage('services');
    setSelectedService(null);
    setSelectedProvider(null);
    setSelectedAppointment(null);
    setBookingId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const copyBookingId = () => {
    navigator.clipboard.writeText(bookingId);
    alert('ID rezervacije kopiran!');
  };

  const handleShare = async () => {
    const shareUrl = `https://medvista.online/${tenantName}`;
    const shareData = {
      title: clinicInfo.name,
      text: `Zakažite pregled u ${clinicInfo.name}`,
      url: shareUrl
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link kopiran u clipboard!');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const goToPage = (page) => {
    if (page === 'services') {
      setCurrentPage('services');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (page === 'providers' && selectedService) {
      setCurrentPage('providers');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (page === 'calendar' && selectedService && selectedProvider) {
      setCurrentPage('calendar');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (page === 'form' && selectedService && selectedProvider && selectedAppointment) {
      setCurrentPage('form');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
    const months = ['januar', 'februar', 'mart', 'april', 'maj', 'jun', 'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
    return `${days[date.getDay()]}, ${date.getDate()}. ${months[date.getMonth()]}`;
  };

  const goToPreviousDate = () => {
    if (currentDateIndex > 0) {
      setSelectedDate(availableDates[currentDateIndex - 1]);
    }
  };

  const goToNextDate = () => {
    if (currentDateIndex < availableDates.length - 1) {
      setSelectedDate(availableDates[currentDateIndex + 1]);
    }
  };

  const getCurrentDayStatus = () => {
    const today = new Date();
    const dayName = ['nedelja', 'ponedeljak', 'utorak', 'sreda', 'četvrtak', 'petak', 'subota'][today.getDay()];
    const workingDay = clinicInfo.workingHours[dayName];
    
    if (!workingDay || !workingDay.isOpen) return { status: 'closed', text: 'Zatvoreno' };
    
    const now = today.getHours() * 60 + today.getMinutes();
    const [openH, openM] = workingDay.open.split(':').map(Number);
    const [closeH, closeM] = workingDay.close.split(':').map(Number);
    const openTime = openH * 60 + openM;
    const closeTime = closeH * 60 + closeM;
    
    if (now < openTime) return { status: 'before', text: `Otvara se u ${workingDay.open}` };
    if (now > closeTime) return { status: 'closed', text: 'Zatvoreno' };
    return { status: 'open', text: `Otvoreno do ${workingDay.close}` };
  };

  const dayStatus = getCurrentDayStatus();

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 group-hover:bg-blue-100',
    red: 'bg-red-50 text-red-700 group-hover:bg-red-100',
    pink: 'bg-pink-50 text-pink-700 group-hover:bg-pink-100',
    purple: 'bg-purple-50 text-purple-700 group-hover:bg-purple-100',
    green: 'bg-green-50 text-green-700 group-hover:bg-green-100',
    orange: 'bg-orange-50 text-orange-700 group-hover:bg-orange-100'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Učitavanje podataka klinike...</p>
        </div>
      </div>
    );
  }

  if (tenantInactive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center border border-gray-100">
            <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-orange-600" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Klinika nije aktivna
            </h1>
            
            <p className="text-lg text-gray-600 mb-8">
              Ova klinika trenutno nije aktivirana na MedVista platformi. Molimo kontaktirajte kliniku direktno.
            </p>
            
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 mb-6 border border-blue-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Kontaktirajte nas
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Za više informacija o aktivaciji klinike na MedVista platformi, kontaktirajte nas na:
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="mailto:office@tecomatic.rs" 
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <Mail className="w-5 h-5" />
                  office@tecomatic.rs
                </a>
                <a 
                  href="https://tecomatic.rs" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all border-2 border-blue-600"
                >
                  <Globe className="w-5 h-5" />
                  Posetite sajt
                </a>
              </div>
            </div>
            
            <div className="text-sm text-gray-500">
              <p>Powered by <span className="font-semibold text-blue-600">MedVista.online</span></p>
              <p className="mt-1">Created by <a href="https://tecomatic.rs" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:text-blue-800">Tecomatic.rs</a></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tenantNotFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center border border-gray-100">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-red-600" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Klinika nije pronađena
            </h1>
            
            <p className="text-lg text-gray-600 mb-8">
              Tražena klinika ne postoji ili nije dostupna na ovoj adresi.
            </p>
            
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 mb-6 border border-blue-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Zainteresovani ste za MedVista platformu?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                MedVista je moderna platforma za online zakazivanje termina u zdravstvenim ustanovama. 
                Ako želite da vaša klinika bude dostupna na ovoj platformi, kontaktirajte nas!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="mailto:office@tecomatic.rs" 
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <Mail className="w-5 h-5" />
                  office@tecomatic.rs
                </a>
                <a 
                  href="https://tecomatic.rs" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all border-2 border-blue-600"
                >
                  <Globe className="w-5 h-5" />
                  Posetite sajt
                </a>
              </div>
            </div>
            
            <div className="text-sm text-gray-500">
              <p>Powered by <span className="font-semibold text-blue-600">MedVista.online</span></p>
              <p className="mt-1">Created by <a href="https://tecomatic.rs" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:text-blue-800">Tecomatic.rs</a></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const SidebarContent = () => (
    <div className="p-6">
      <div className="mb-6">
        <button onClick={goToHome} className="w-full text-center">
          {clinicInfo.logoUrl ? (
            <div className="w-28 h-28 mx-auto mb-4 rounded-full overflow-hidden shadow-lg border-4 border-white hover:scale-105 transition-transform cursor-pointer bg-white">
              <img 
                src={clinicInfo.logoUrl} 
                alt={`${clinicInfo.name} logo`}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-28 h-28 mx-auto bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-600 rounded-full flex items-center justify-center mb-4 shadow-lg border-4 border-white hover:scale-105 transition-transform cursor-pointer">
              <span className="text-3xl font-bold text-white">
                {clinicInfo.name ? clinicInfo.name.charAt(0).toUpperCase() : 'K'}
              </span>
            </div>
          )}
          <h2 className="text-2xl font-bold text-gray-900 text-center hover:text-blue-600 transition-colors">{clinicInfo.name}</h2>
          <p className="text-sm text-gray-500 mt-1 text-center">{clinicInfo.slogan}</p>
        </button>
      </div>

      {clinicInfo.about && (
        <div className="mb-6">
          <button 
            onClick={() => setIsAboutOpen(!isAboutOpen)}
            className="w-full px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group"
          >
            <span className="text-sm font-semibold text-gray-700 group-hover:text-blue-600">O nama</span>
            {isAboutOpen ? (
              <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
            )}
          </button>
          {isAboutOpen && (
            <div className="mt-2 px-3 py-2">
              <p className="text-xs text-gray-600 leading-relaxed">{clinicInfo.about}</p>
            </div>
          )}
        </div>
      )}

      <div className={`mb-6 p-3 rounded-xl ${
        dayStatus.status === 'open' ? 'bg-green-50 border border-green-200' :
        dayStatus.status === 'before' ? 'bg-amber-50 border border-amber-200' :
        'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            dayStatus.status === 'open' ? 'bg-green-500 animate-pulse' :
            dayStatus.status === 'before' ? 'bg-amber-500' :
            'bg-red-500'
          }`}></div>
          <span className={`text-sm font-semibold ${
            dayStatus.status === 'open' ? 'text-green-700' :
            dayStatus.status === 'before' ? 'text-amber-700' :
            'text-red-700'
          }`}>
            {dayStatus.text}
          </span>
        </div>
      </div>

      <div className="space-y-3 text-sm mb-6 pb-6 border-b border-gray-200">
        {clinicInfo.location && (
          <a href={clinicInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors group">
            <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <span className="text-gray-700 group-hover:text-blue-700 font-medium block">{clinicInfo.location}</span>
              {clinicInfo.country && <span className="text-xs text-gray-500">{clinicInfo.country}</span>}
            </div>
          </a>
        )}
        {clinicInfo.phone1 && (
          <div className="flex items-start gap-3 p-2">
            <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <a href={`tel:${clinicInfo.phone1}`} className="text-gray-700 hover:text-blue-600 font-medium block">{clinicInfo.phone1}</a>
            </div>
          </div>
        )}
        {clinicInfo.phone2 && (
          <div className="flex items-start gap-3 p-2">
            <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <a href={`tel:${clinicInfo.phone2}`} className="text-gray-700 hover:text-blue-600 font-medium block">{clinicInfo.phone2}</a>
            </div>
          </div>
        )}
        {clinicInfo.email && (
          <a href={`mailto:${clinicInfo.email}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors group">
            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <span className="text-gray-700 group-hover:text-blue-700 font-medium">{clinicInfo.email}</span>
          </a>
        )}
        {clinicInfo.website && (
          <a href={`https://${clinicInfo.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors group">
            <Globe className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <span className="text-gray-700 group-hover:text-blue-700 font-medium">{clinicInfo.website}</span>
          </a>
        )}
        {qrCodeDataUrl && (
          <button onClick={() => setIsQRModalOpen(true)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors group w-full text-left">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            <span className="text-gray-700 group-hover:text-blue-700 font-medium">QR kontakt</span>
          </button>
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2 uppercase tracking-wide">
          <Clock className="w-4 h-4 text-blue-600" />
          Radno vreme
        </h3>
        <div className="space-y-0.5 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-3 border border-gray-200">
          {Object.entries(clinicInfo.workingHours).map(([day, hours]) => {
            const isToday = new Date().getDay() === ['nedelja', 'ponedeljak', 'utorak', 'sreda', 'četvrtak', 'petak', 'subota'].indexOf(day);
            return (
              <div key={day} className={`flex justify-between items-center py-1.5 px-2 rounded-lg ${isToday ? 'bg-blue-100 font-bold' : ''}`}>
                <span className="text-gray-700 capitalize font-medium text-sm">{day}</span>
                <span className={`text-xs font-bold ${hours.isOpen ? 'text-gray-800' : 'text-red-600'}`}>
                  {hours.isOpen ? `${hours.open} - ${hours.close}` : 'Zatvoreno'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Share dugme - samo mobilni */}
      <div className="mt-6 lg:hidden">
        <button 
          onClick={handleShare}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Podeli stranicu
        </button>
      </div>
    </div>
  );

  const QRModal = () => {
    if (!isQRModalOpen || !qrCodeDataUrl) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex items-center justify-center p-4" onClick={() => setIsQRModalOpen(false)}>
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Skeniraj QR kod</h3>
            <button onClick={() => setIsQRModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
          
          <div className="bg-white p-6 rounded-2xl border-2 border-gray-200 mb-6">
            <img 
              src={qrCodeDataUrl} 
              alt="QR kod za kontakt" 
              className="w-full h-auto"
            />
          </div>
          
          <p className="text-center text-gray-600 text-sm mb-4">
            Skenirajte ovaj QR kod da dodate kontakt u vaš telefon
          </p>
          
          <button 
            onClick={() => setIsQRModalOpen(false)}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
          >
            Zatvori
          </button>
        </div>
      </div>
    );
  };

  return (
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* QR Modal */}
      <QRModal />

      <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto hidden lg:block border-r border-gray-100 z-40">
        <SidebarContent />
      </div>

      <div className={`fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsSidebarOpen(false)}></div>
      <div className={`fixed left-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto z-50 lg:hidden transform transition-transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex justify-end p-4">
          <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>
        <SidebarContent />
      </div>

      <div className="lg:ml-80 flex-1 flex flex-col">
        <div className="lg:hidden bg-white shadow-sm p-4 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setIsSidebarOpen(true)} className="p-2 hover:bg-gray-100 rounded-lg">
              <Menu className="w-6 h-6" />
            </button>
            <button onClick={goToHome} className="flex items-center gap-3 flex-1">
              {clinicInfo.logoUrl ? (
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 shadow-md border-2 border-white hover:scale-105 transition-transform bg-white">
                  <img 
                    src={clinicInfo.logoUrl} 
                    alt={`${clinicInfo.name} logo`}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md border-2 border-white hover:scale-105 transition-transform">
                  <span className="text-sm font-bold text-white">
                    {clinicInfo.name ? clinicInfo.name.charAt(0).toUpperCase() : 'K'}
                  </span>
                </div>
              )}
              <div className="flex-1 text-left">
                <h2 className="text-lg font-bold text-gray-800 hover:text-blue-600 transition-colors">{clinicInfo.name}</h2>
                <p className="text-xs text-gray-600">{clinicInfo.slogan}</p>
              </div>
            </button>
            {clinicInfo.phone1 && (
              <a href={`tel:${clinicInfo.phone1}`} className="p-2 hover:bg-blue-50 rounded-lg transition-colors">
                <Phone className="w-6 h-6 text-blue-600" />
              </a>
            )}
          </div>
        </div>

        {/* Alert Banner - Prikazuje se samo ako postoji alert */}
        {clinicInfo.alert && clinicInfo.alert.trim() !== '' && (
          <div className="lg:ml-0 bg-amber-50 border-b-2 border-amber-200 px-4 py-3 sticky top-0 lg:top-0 z-20">
            <div className="max-w-6xl mx-auto flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">{clinicInfo.alert}</p>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-6xl mx-auto p-4 md:p-8 flex-1 w-full">
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2 text-sm">
              <button onClick={() => goToPage('services')} className={`flex items-center ${currentPage === 'services' ? 'text-blue-600 font-semibold' : 'text-gray-400 hover:text-gray-600'} transition-colors`}>
                <span className={`w-9 h-9 rounded-full flex items-center justify-center mr-2 font-bold ${currentPage === 'services' ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-200 hover:bg-gray-300'} transition-all cursor-pointer`}>1</span>
                <span className="hidden sm:inline">Usluga</span>
              </button>
              <div className="w-8 h-1 bg-gray-300 rounded"></div>
              <button onClick={() => goToPage('providers')} disabled={!selectedService} className={`flex items-center ${currentPage === 'providers' ? 'text-blue-600 font-semibold' : selectedService ? 'text-gray-400 hover:text-gray-600' : 'text-gray-300 cursor-not-allowed'} transition-colors`}>
                <span className={`w-9 h-9 rounded-full flex items-center justify-center mr-2 font-bold ${currentPage === 'providers' ? 'bg-blue-600 text-white shadow-lg' : selectedService ? 'bg-gray-200 hover:bg-gray-300 cursor-pointer' : 'bg-gray-200'} transition-all`}>2</span>
                <span className="hidden sm:inline">Pruzaoc</span>
              </button>
              <div className="w-8 h-1 bg-gray-300 rounded"></div>
              <button onClick={() => goToPage('calendar')} disabled={!selectedService || !selectedProvider} className={`flex items-center ${currentPage === 'calendar' ? 'text-blue-600 font-semibold' : (selectedService && selectedProvider) ? 'text-gray-400 hover:text-gray-600' : 'text-gray-300 cursor-not-allowed'} transition-colors`}>
                <span className={`w-9 h-9 rounded-full flex items-center justify-center mr-2 font-bold ${currentPage === 'calendar' ? 'bg-blue-600 text-white shadow-lg' : (selectedService && selectedProvider) ? 'bg-gray-200 hover:bg-gray-300 cursor-pointer' : 'bg-gray-200'} transition-all`}>3</span>
                <span className="hidden sm:inline">Termin</span>
              </button>
              <div className="w-8 h-1 bg-gray-300 rounded"></div>
              <button onClick={() => goToPage('form')} disabled={!selectedService || !selectedProvider || !selectedAppointment} className={`flex items-center ${currentPage === 'form' ? 'text-blue-600 font-semibold' : (selectedService && selectedProvider && selectedAppointment) ? 'text-gray-400 hover:text-gray-600' : 'text-gray-300 cursor-not-allowed'} transition-colors`}>
                <span className={`w-9 h-9 rounded-full flex items-center justify-center mr-2 font-bold ${currentPage === 'form' ? 'bg-blue-600 text-white shadow-lg' : (selectedService && selectedProvider && selectedAppointment) ? 'bg-gray-200 hover:bg-gray-300 cursor-pointer' : 'bg-gray-200'} transition-all`}>4</span>
                <span className="hidden sm:inline">Podaci</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 border border-gray-100">
            {currentPage === 'services' && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Odaberite uslugu</h2>
                <p className="text-gray-600 mb-8 text-center">Kliknite na medicinsku uslugu koju želite da zakažete</p>
                
                {services.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Nema dostupnih usluga</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {services.map(service => {
                      const Icon = service.icon;
                      return (
                        <button key={service.id} onClick={() => handleServiceSelect(service)} className="p-6 border-2 border-gray-200 rounded-2xl hover:border-blue-500 hover:shadow-2xl transition-all text-left group">
                          <div className="flex flex-col gap-3">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${colorClasses[service.color] || colorClasses['blue']}`}>
                              <Icon className="w-7 h-7" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-800 mb-1 text-lg">{service.name}</h3>
                              <p className="text-xs text-gray-500 mb-3">{service.description}</p>
                              <div className="flex items-center justify-between text-sm">
                                {service.duration && (
                                  <span className="text-gray-600">{service.duration} min</span>
                                )}
                                {service.price && (
                                  <span className="font-bold text-blue-600">{service.price}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {currentPage === 'providers' && (
              <div>
                <button onClick={() => setCurrentPage('services')} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 text-sm font-medium">
                  <ChevronLeft className="w-4 h-4" />
                  Promeni uslugu
                </button>

                <div className="mb-8 text-center">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Odaberite pruzaoca</h2>
                  <p className="text-gray-600">
                    {selectedService.name}
                    {selectedService.price && ` • ${selectedService.price}`}
                  </p>
                </div>
                
                {providers.length === 0 ? (
                  <div className="text-center py-12">
                    <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Nema dostupnih pruzalaca za ovu uslugu</p>
                  </div>
                ) : (
                  <div className="max-w-2xl mx-auto space-y-3">
                    {providers.map(provider => (
                      <button 
                        key={provider.id} 
                        onClick={() => handleProviderSelect(provider)} 
                        className="w-full p-6 border-2 border-gray-200 rounded-2xl hover:border-blue-500 hover:shadow-xl transition-all text-left group bg-white"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 text-xl mb-2">
                              {provider.title && <span className="text-blue-600">{provider.title} </span>}
                              {provider.firstName} {provider.lastName}
                            </h3>
                            {provider.specialization && (
                              <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                                <Stethoscope className="w-4 h-4 text-blue-500" />
                                {provider.specialization}
                              </p>
                            )}
                            <p className="text-sm text-blue-600 font-semibold flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Trajanje pregleda: {provider.duration} min
                            </p>
                          </div>
                          <ChevronRight className="w-7 h-7 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0 ml-4" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {currentPage === 'calendar' && (
              <div>
                <button onClick={() => setCurrentPage('providers')} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 text-sm font-medium">
                  <ChevronLeft className="w-4 h-4" />
                  Promeni pruzaoca
                </button>

                <div className="mb-8 text-center">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Odaberite termin</h2>
                  <p className="text-gray-600">
                    {selectedService.name} • {selectedProvider.title ? selectedProvider.title + ' ' : ''}{selectedProvider.firstName} {selectedProvider.lastName} • {selectedProvider.duration} min
                    {selectedService.price && ` • ${selectedService.price}`}
                  </p>
                </div>
                
                <div className="flex items-center justify-between mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4">
                  <button onClick={goToPreviousDate} disabled={currentDateIndex === 0} className="p-3 rounded-xl hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                    <ChevronLeft className="w-6 h-6 text-blue-600" />
                  </button>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="text-lg font-bold text-gray-800">{formatDate(selectedDate)}</span>
                  </div>
                  <button onClick={goToNextDate} disabled={currentDateIndex === availableDates.length - 1} className="p-3 rounded-xl hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                    <ChevronRight className="w-6 h-6 text-blue-600" />
                  </button>
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2">
                  {filteredAppointments.map(appointment => (
                    <button key={appointment.id} onClick={() => handleAppointmentClick(appointment)} disabled={!appointment.available} className={`py-3 px-2 rounded-xl text-sm font-semibold transition-all ${appointment.available ? 'bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                      {appointment.time}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentPage === 'form' && (
              <div>
                <button onClick={() => setCurrentPage('calendar')} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 text-sm font-medium">
                  <ChevronLeft className="w-4 h-4" />
                  Promeni termin
                </button>

                <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Unesite vaše podatke</h2>
                <p className="text-gray-600 mb-8 text-center">
                  {selectedService.name} • {selectedProvider.title ? selectedProvider.title + ' ' : ''}{selectedProvider.firstName} {selectedProvider.lastName} • {formatDate(selectedAppointment.date)} u {selectedAppointment.time}
                </p>

                <div className="space-y-5 max-w-lg mx-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Ime *</label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleFormChange} required className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Ime" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Prezime *</label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleFormChange} required className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Prezime" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Godište *</label>
                      <input type="text" name="birthYear" value={formData.birthYear} onChange={handleFormChange} required maxLength="4" className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="1990" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Broj telefona *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0651234567" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input type="email" name="email" value={formData.email} onChange={handleFormChange} className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="vas@email.com (opciono)" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Napomena</label>
                    <textarea name="notes" value={formData.notes} onChange={handleFormChange} rows="4" className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" placeholder="Dodatne informacije... (opciono)"></textarea>
                  </div>

                  {validationError && (
                    <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm font-medium flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{validationError}</span>
                    </div>
                  )}

                  <button onClick={handleFormSubmit} className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg">
                    Potvrdi zakazivanje
                  </button>
                </div>
              </div>
            )}

            {currentPage === 'confirmation' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{clinicInfo.messages.confirmationTitle}</h2>
                <p className="text-gray-600 mb-4">{clinicInfo.messages.confirmationSubtitle} {formData.email ? `na ${formData.email}` : 'SMS-om na vaš telefon'}</p>
                
                <div className="inline-block bg-white border-2 border-blue-600 px-8 py-4 rounded-xl mb-2 shadow-sm relative">
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1 text-blue-600">ID Rezervacije</p>
                  <div className="flex items-center gap-3">
                    <p className="text-2xl font-mono font-bold text-gray-800">{bookingId}</p>
                    <button onClick={copyBookingId} className="p-2 hover:bg-blue-50 rounded-lg transition-colors" title="Kopiraj ID">
                      <Copy className="w-5 h-5 text-blue-600" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-6">Kliknite na ikonicu da kopirate ID</p>

                <div className="bg-blue-50 rounded-lg p-6 mb-4 text-left max-w-md mx-auto">
                  <h3 className="font-semibold text-gray-800 mb-4">Detalji rezervacije</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Usluga:</span>
                      <span className="font-medium">{selectedService.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pruzaoc:</span>
                      <span className="font-medium">{selectedProvider.title ? selectedProvider.title + ' ' : ''}{selectedProvider.firstName} {selectedProvider.lastName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trajanje:</span>
                      <span className="font-medium">{selectedProvider.duration} min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Datum:</span>
                      <span className="font-medium">{formatDate(selectedAppointment.date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Vreme:</span>
                      <span className="font-medium">{selectedAppointment.time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pacijent:</span>
                      <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Godište:</span>
                      <span className="font-medium">{formData.birthYear}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Telefon:</span>
                      <span className="font-medium">{formData.phone}</span>
                    </div>
                    {formData.email && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{formData.email}</span>
                      </div>
                    )}
                    {formData.notes && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Napomena:</span>
                        <span className="font-medium">{formData.notes}</span>
                      </div>
                    )}
                    {selectedService.price && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cena:</span>
                        <span className="font-medium text-blue-600">{selectedService.price}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 max-w-md mx-auto">
                  <p className="text-sm text-green-800">
                    <strong>Odustajanje i izmene:</strong> U slučaju odustajanja ili potrebe za izmenom termina, molimo pozovite nas na{' '}
                    <a href={`tel:${clinicInfo.phone1}`} className="font-bold underline hover:text-green-900">
                      {clinicInfo.phone1}
                    </a>
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                  <p className="text-sm text-amber-800">
                    <strong>Napomena:</strong> {clinicInfo.messages.confirmationNote}
                  </p>
                </div>

                <button onClick={resetBooking} className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                  Zakaži novi pregled
                </button>
              </div>
            )}
          </div>
        </div>

        <footer className="bg-white border-t border-gray-200 py-4 px-6 text-center text-sm text-gray-600 mt-auto">
          Created and hosted by <a href="https://tecomatic.rs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-semibold">Tecomatic.rs</a>
        </footer>
      </div>
    </div>
  );
};

export default App;